import { calibration } from '@edh/shared';
import { useStore } from '../lib/store';

/**
 * Whether the odds the table is shown are any good.
 *
 * Every game freezes the prediction it was played at, and this is the only
 * reason that is worth doing: a forecast nobody ever marks is decoration. Seats
 * given 70% should win about 70% of the time. If they win 45%, the number on
 * the setup screen is confident and wrong, and the honest response is to say so
 * on the screen rather than keep showing it.
 *
 * Hidden until there is enough to say anything. Five games of predictions will
 * produce a chart, and the chart will be noise.
 */
const ENOUGH = 20;

export function Calibration() {
  const { state } = useStore();
  const result = calibration(state.matches);
  if (result.n < ENOUGH) return null;

  const useful = result.buckets.filter((b) => b.n > 0);

  return (
    <>
      <div className="section-head">
        <h2>Polymarket accuracy</h2>
        <span className="hint num">{result.n}</span>
      </div>

      <div className="panel">
        <p className="hint" style={{ margin: '0 0 12px' }}>
          What the odds said, against what happened. Bars that match are a model worth
          reading; a short bar under a tall one means the app was too confident.
        </p>

        <div className="calib">
          {useful.map((b) => (
            <div className="calib-row" key={b.from}>
              <span className="calib-band num">
                {Math.round(b.from * 100)}–{Math.round(b.to * 100)}%
              </span>
              <span className="calib-bars">
                <span className="calib-bar said" style={{ width: `${b.predicted * 100}%` }}>
                  <span className="calib-value">{Math.round(b.predicted * 100)}%</span>
                </span>
                <span className="calib-bar did" style={{ width: `${b.actual * 100}%` }}>
                  <span className="calib-value">{Math.round(b.actual * 100)}%</span>
                </span>
              </span>
              <span className="calib-n num">{b.n}</span>
            </div>
          ))}
        </div>

        <div className="calib-key">
          <span>
            <span className="calib-swatch said" /> said
          </span>
          <span>
            <span className="calib-swatch did" /> happened
          </span>
        </div>

        {/*
         * The verdict, in words, because the number is meaningless without one.
         *
         * Measured against what these same games score by splitting the odds
         * evenly — not against 0.25, which is the figure for a two-player coin
         * flip and would let an uninformed model look clever purely by playing
         * four-handed.
         */}
        <div className="calib-verdict">
          <span className={`calib-grade${result.skill > 0.05 ? ' good' : result.skill < -0.02 ? ' bad' : ''}`}>
            {result.skill > 0.15
              ? 'Reading the table well'
              : result.skill > 0.05
                ? 'Better than guessing'
                : result.skill < -0.02
                  ? 'Worse than guessing'
                  : 'No better than guessing'}
          </span>
          <span className="hint">
            Brier <span className="num">{result.brier.toFixed(3)}</span> against{' '}
            <span className="num">{result.baseline.toFixed(3)}</span> for splitting every game
            evenly. Lower is better.
          </span>
          {result.skill <= 0.05 && (
            <span className="hint">
              Until this beats the baseline, treat the ratings as a rough ordering rather than a
              measurement — and the ranks as decoration on top of it.
            </span>
          )}
        </div>
      </div>
    </>
  );
}
